import { ChangeDetectorRef, Directive, EventEmitter, inject, Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil } from 'rxjs';
import { LatLngBounds } from 'leaflet';
import { SelectionService } from '../../services/selection.service';
import { StorageService } from '../../services/storage/storage.service';
import { FilterProvider } from '../interfaces/filter-provider.interface';
import { FILTER_PROVIDER } from '../tokens';
import { ProjectPointsService } from '../../map/project/project-points.service';

export type ListFilterMode = 'all' | 'map' | 'selection';

/**
 * Abstrakte Basisklasse für Listen-Panel-Komponenten.
 * Stellt generische Infrastruktur bereit:
 * - 3-stufiges Filtern (räumlich → benannter Filter → Textsuche)
 * - Paginierung ("Mehr laden")
 * - Auswahl-Management
 * - Sortierinfrastruktur
 * - Einstellungs-Persistenz
 */
@Directive()
export abstract class BaseListPanelComponent implements OnInit, OnDestroy, OnChanges {
	protected modalService = inject(NgbModal);
	protected cd = inject(ChangeDetectorRef);
	protected destroy$ = new Subject<void>();

	@Input() bps: any[] = [];
	@Input() pointsFilter: boolean[] = [];
	@Input() boundsFilter: boolean[] = [];
	@Input() currentBounds?: LatLngBounds;
	@Input() activeId?: string;
	@Input() showProjectPointsFilter = false;

	@Output() pointSelected = new EventEmitter<{ event: Event; bp: any }>();
	@Output() pointsDeleted = new EventEmitter<void>();
	@Output() filterApplied = new EventEmitter<string>();

	// Filter-Modus
	listFilterMode: ListFilterMode = 'map';

	// Suchfeld und Filter-Dropdown
	searchText = '';
	filterNames: string[] = [];
	currentFilterName: string | null = null;
	mapFilterStale = false;

	// Einstellungen
	onlyWithProtocol = false;
	sortToTop: string = 'none';
	sortOrder: string = 'type';

	// Sortierung und Paginierung
	sortedBpsCache: { bp: any; originalIndex: number }[] = [];
	visibleBps: { bp: any; originalIndex: number }[] = [];
	protected readonly ITEMS_PER_PAGE = 500;
	protected currentLimit = 500;
	filteredCount = 0;
	hasMoreItems = false;
	totalVisibleCount = 0;

	constructor(
		public selectionService: SelectionService,
		@Inject(FILTER_PROVIDER) protected fs: FilterProvider,
		protected storage: StorageService,
		public pps: ProjectPointsService
	) {
		// Selection-Änderungen abonnieren
		this.selectionService.selectionChanged.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.cd.markForCheck();
		});

		// Filter-Namen laden
		this.loadFilterNames();
		this.fs.filtersUpdated.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.loadFilterNames();
			this.cd.markForCheck();
		});

		// Gespeicherte Einstellungen laden
		this.currentFilterName = this.storage.getSync('currentFilter');
		this.searchText = this.storage.getSync('searchText') || '';
		const savedListFilterMode = this.storage.getSync('listFilterMode') as ListFilterMode;
		if (savedListFilterMode) {
			this.listFilterMode = savedListFilterMode;
		}
		this.onlyWithProtocol = this.storage.getSync('listOnlyWithProtocol') === true;
		const savedSortToTop = this.storage.getSync('listSortToTop');
		if (savedSortToTop) {
			this.sortToTop = savedSortToTop;
		}
		const savedSortOrder = this.storage.getSync('listSortOrder');
		if (savedSortOrder) {
			this.sortOrder = savedSortOrder;
		}
	}

	ngOnInit() {
		this.updateSortedBpsCache();
		this.updateFilteredCount();
		this.updateVisibleBps();
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['bps'] || changes['pointsFilter'] || changes['boundsFilter']) {
			this.resetLimit();
			this.updateSortedBpsCache();
			this.updateFilteredCount();
			this.updateVisibleBps();
		}
	}

	// --- Abstrakte Methoden (app-spezifisch) ---

	/** Prüft ob ein Punkt der Textsuche entspricht */
	protected abstract matchesSearch(bp: any): boolean;

	/** Vergleicht zwei Punkte für die Sortierung */
	protected abstract compareBps(a: any, b: any, sortOrder: string): number;

	/** Gibt die ID eines Punktes zurück */
	abstract getItemId(bp: any): string;

	/** Prüft ob ein Punkt ein vollständiges Protokoll hat (für "nur mit Protokoll"-Filter) */
	protected abstract hasProtocol(bp: any): boolean;

	// --- Generische Methoden ---

	trackByBpId = (index: number, item: { bp: any; originalIndex: number }): string => {
		return this.getItemId(item.bp);
	}

	select(event: Event, bp: any) {
		this.pointSelected.emit({ event, bp });
	}

	setListFilterMode(mode: ListFilterMode) {
		this.listFilterMode = mode;
		this.storage.setSync('listFilterMode', mode);
		this.resetLimit();
		this.updateVisibleBps();
		this.cd.markForCheck();
	}

	isVisibleInList(i: number): boolean {
		if (!this.bps || i >= this.bps.length) return false;
		const bp = this.bps[i];

		// 0. Nur Punkte mit Protokoll
		if (this.onlyWithProtocol && !this.hasProtocol(bp)) {
			return false;
		}

		// 1. Filter (aus Filtermenü)
		if (this.currentFilterName) {
			if (!this.fs.filteredDocIds.has(this.getItemId(bp))) return false;
		} else {
			switch (this.listFilterMode) {
				case 'map':
					if (!(this.boundsFilter[i] ?? true)) return false;
					break;
				case 'selection':
					if (!this.selectionService.isSelected(this.getItemId(bp))) return false;
					break;
				case 'all':
				default:
					break;
			}
		}

		// 2. Schnellsuche
		if (!this.matchesSearch(bp)) return false;

		return true;
	}

	// Statistik
	getMapCount(): number {
		if (!this.boundsFilter) return 0;
		return this.boundsFilter.filter(b => b).length;
	}

	// Suchfeld
	onSearchTextChange(text: string) {
		this.searchText = text;
		this.storage.setSync('searchText', text);
		this.updateVisibleBps();
		this.cd.markForCheck();
	}

	clearSearch() {
		this.searchText = '';
		this.storage.removeSync('searchText');
		this.updateVisibleBps();
		this.cd.markForCheck();
	}

	// Filter
	loadFilterNames() {
		this.fs.list_keys().then((names: string[]) => {
			this.filterNames = names;
		});
	}

	clearFilter() {
		this.currentFilterName = null;
		this.storage.removeSync('currentFilter');
		this.fs.resetFilter();
		this.updateFilteredCount();
		this.updateVisibleBps();
		this.cd.markForCheck();
	}

	applyProjectPointsFilter() {
		this.currentFilterName = 'Projektpunkte';
		this.storage.setSync('currentFilter', 'Projektpunkte');
		this.fs.setFilteredDocIds(this.pps.allProjectPointIds());
		this.updateFilteredCount();
		this.updateVisibleBps();
		this.filterApplied.emit('Projektpunkte');
		this.cd.markForCheck();
	}

	// Auswahl
	addVisibleToSelection() {
		if (!this.bps) return;
		const ids: string[] = [];
		this.bps.forEach((bp, i) => {
			if (this.isVisibleInList(i)) {
				ids.push(this.getItemId(bp));
			}
		});
		this.selectionService.add(ids);
	}

	removeVisibleFromSelection() {
		if (!this.bps) return;
		const ids: string[] = [];
		this.bps.forEach((bp, i) => {
			if (this.isVisibleInList(i) && this.selectionService.isSelected(this.getItemId(bp))) {
				ids.push(this.getItemId(bp));
			}
		});
		this.selectionService.removeMultiple(ids);
	}

	invertVisibleSelection() {
		if (!this.bps) return;
		const toAdd: string[] = [];
		const toRemove: string[] = [];
		this.bps.forEach((bp, i) => {
			if (this.isVisibleInList(i)) {
				const id = this.getItemId(bp);
				if (this.selectionService.isSelected(id)) {
					toRemove.push(id);
				} else {
					toAdd.push(id);
				}
			}
		});
		this.selectionService.removeMultiple(toRemove);
		this.selectionService.add(toAdd);
	}

	clearSelection() {
		this.selectionService.clear();
	}

	// Paginierung
	loadMore() {
		this.currentLimit += this.ITEMS_PER_PAGE;
		this.updateVisibleBps();
		this.cd.markForCheck();
	}

	loadAll() {
		this.currentLimit = this.totalVisibleCount;
		this.updateVisibleBps();
		this.cd.markForCheck();
	}

	// Einstellungen
	onSettingsChange() {
		this.storage.setSync('listOnlyWithProtocol', this.onlyWithProtocol);
		this.storage.setSync('listSortToTop', this.sortToTop);
		this.storage.setSync('listSortOrder', this.sortOrder);
		this.updateSortedBpsCache();
		this.updateVisibleBps();
		this.cd.markForCheck();
	}

	// --- Interne Methoden ---

	protected updateSortedBpsCache() {
		if (!this.bps || this.bps.length === 0) {
			this.sortedBpsCache = [];
			return;
		}

		const items = this.bps.map((bp, i) => ({ bp, originalIndex: i }));

		items.sort((a, b) => {
			// Nach oben sortieren
			if (this.sortToTop !== 'none') {
				let aTop = 0;
				let bTop = 0;
				switch (this.sortToTop) {
					case 'selected':
						aTop = this.selectionService.isSelected(this.getItemId(a.bp)) ? 1 : 0;
						bTop = this.selectionService.isSelected(this.getItemId(b.bp)) ? 1 : 0;
						break;
					case 'assigned':
						aTop = this.pps.isAssigned(this.getItemId(a.bp)) ? 1 : 0;
						bTop = this.pps.isAssigned(this.getItemId(b.bp)) ? 1 : 0;
						break;
					case 'filtered':
						aTop = this.pointsFilter[a.originalIndex] ? 1 : 0;
						bTop = this.pointsFilter[b.originalIndex] ? 1 : 0;
						break;
				}
				if (aTop !== bTop) return bTop - aTop;
			}

			return this.compareBps(a.bp, b.bp, this.sortOrder);
		});

		this.sortedBpsCache = items;
	}

	protected updateFilteredCount() {
		this.filteredCount = this.pointsFilter?.filter(f => f).length || 0;
	}

	protected updateVisibleBps() {
		if (!this.sortedBpsCache || this.sortedBpsCache.length === 0) {
			this.visibleBps = [];
			this.hasMoreItems = false;
			this.totalVisibleCount = 0;
			return;
		}

		const visible: { bp: any; originalIndex: number }[] = [];
		for (const item of this.sortedBpsCache) {
			if (this.isVisibleInList(item.originalIndex)) {
				visible.push(item);
			}
		}

		this.totalVisibleCount = visible.length;
		this.hasMoreItems = visible.length > this.currentLimit;
		this.visibleBps = visible.slice(0, this.currentLimit);
	}

	protected resetLimit() {
		this.currentLimit = this.ITEMS_PER_PAGE;
	}
}
